/**
 * Copy a single askRichard answer to the clipboard as a PNG, so it can be pasted
 * straight into Slack. This is the move that recruits people who never open the
 * tool, so it has to be reliable and produce a clean, on-brand image.
 *
 * Rendering uses html2canvas (already a dependency, imported the same way as
 * conversationPdfExporter) and the answer is captured with the theme paper colour
 * behind it, so the PNG is a solid card and never transparent (which reads black
 * in dark mode). A padding frame keeps the answer off the edges.
 *
 * The pure, testable pieces (filename, capture options, clipboard support probe)
 * are separated from the thin async html2canvas + clipboard call so the fallback
 * and guard branches can be unit tested with mocks.
 */

/** Device-pixel scale for the capture. 2 keeps the image crisp on retina and in Slack. */
export const ANSWER_IMAGE_SCALE = 2;
/** CSS-px padding frame so the answer is not rendered edge-to-edge. */
export const ANSWER_IMAGE_PADDING_PX = 24;
/** Number of message-id characters used to disambiguate download filenames. */
export const ANSWER_IMAGE_FILENAME_ID_LENGTH = 8;
const ANSWER_IMAGE_FILENAME_PREFIX = 'askrichard-answer';
/** Defer revokeObjectURL until after the click(): Firefox can abort the download otherwise. */
const REVOKE_OBJECT_URL_DELAY_MS = 1000;

/** html2canvas' callable shape, kept loose to avoid coupling to its types and to ease mocking. */
type Html2CanvasFn = (el: HTMLElement, opts?: Record<string, unknown>) => Promise<HTMLCanvasElement>;

export type AnswerImageExportStatus = 'copied' | 'downloaded' | 'error';

export interface AnswerImageExportResult {
  status: AnswerImageExportStatus;
  /** Present on 'error' so callers can log the underlying cause. */
  error?: unknown;
}

export interface AnswerImageCaptureOptions {
  /** Solid background colour for the PNG (the theme paper colour) so it is not transparent. */
  backgroundColor: string;
  /** Device-pixel scale. Defaults to ANSWER_IMAGE_SCALE. */
  scale?: number;
  /** Padding frame in CSS px. Defaults to ANSWER_IMAGE_PADDING_PX. */
  padding?: number;
}

/**
 * Build the html2canvas options for a padded, solid-background capture of `node`.
 * The padding is produced by rendering the element inset from a larger canvas: a
 * negative x/y origin plus extra width/height leaves a frame filled with
 * backgroundColor on every side. Kept pure so the geometry can be unit tested.
 */
export function buildCaptureOptions(
  node: Pick<HTMLElement, 'offsetWidth' | 'offsetHeight'>,
  options: AnswerImageCaptureOptions,
): Record<string, unknown> {
  const padding = options.padding ?? ANSWER_IMAGE_PADDING_PX;
  return {
    backgroundColor: options.backgroundColor,
    scale: options.scale ?? ANSWER_IMAGE_SCALE,
    logging: false,
    useCORS: true,
    x: -padding,
    y: -padding,
    width: node.offsetWidth + padding * 2,
    height: node.offsetHeight + padding * 2,
  };
}

/** A timestamped PNG filename for the download fallback, optionally tagged with a short message id. */
export function buildAnswerImageFilename(messageId?: string, now: Date = new Date()): string {
  const timestamp = now.toISOString().replace(/[-:.]/g, '');
  const parts = [ANSWER_IMAGE_FILENAME_PREFIX];
  const idShort = messageId?.slice(0, ANSWER_IMAGE_FILENAME_ID_LENGTH);
  if (idShort) {
    parts.push(idShort);
  }
  parts.push(timestamp);
  return `${parts.join('-')}.png`;
}

/**
 * Whether this browser can write an image to the clipboard. Older browsers lack
 * ClipboardItem or the async clipboard write, so callers fall back to a download.
 */
export function isClipboardImageWriteSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    Boolean(navigator.clipboard) &&
    typeof navigator.clipboard.write === 'function' &&
    typeof globalThis.ClipboardItem !== 'undefined'
  );
}

async function loadHtml2Canvas(): Promise<Html2CanvasFn> {
  const mod = await import('html2canvas');
  return (mod.default ?? mod) as unknown as Html2CanvasFn;
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to render the answer to an image'));
      }
    }, 'image/png');
  });
}

/**
 * Render `node` to a PNG blob. Built as a promise chain (no await) so the caller
 * can hand this promise straight to a ClipboardItem and issue the clipboard write
 * synchronously inside the click gesture.
 */
function renderNodeToPngBlob(
  node: HTMLElement,
  options: AnswerImageCaptureOptions,
  load: () => Promise<Html2CanvasFn>,
): Promise<Blob> {
  return load()
    .then((html2canvasFn) => html2canvasFn(node, buildCaptureOptions(node, options)))
    .then((canvas) => canvasToPngBlob(canvas));
}

function downloadBlob(blob: Blob, filename: string): void {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_OBJECT_URL_DELAY_MS);
}

export interface CopyAnswerAsImageParams {
  /** The DOM node for the answer content. When null the call is a no-op error. */
  node: HTMLElement | null;
  /** Theme paper colour so the PNG has a solid, on-brand background. */
  backgroundColor: string;
  /** Filename for the download fallback. Defaults to a timestamped name. */
  filename?: string;
  /** Injectable html2canvas loader (tests). */
  loadHtml2CanvasFn?: () => Promise<Html2CanvasFn>;
  /** Injectable download helper (tests). */
  downloadFn?: (blob: Blob, filename: string) => void;
}

/**
 * Render `node` to a PNG and write it to the clipboard. Falls back to a download
 * when the clipboard image API is unsupported or denied, so the user always gets
 * the image. Returns a status the caller maps to a snackbar; never throws.
 *
 * The render is kicked off synchronously (as a Promise<Blob>) and, when the
 * clipboard path is available, handed straight to ClipboardItem so
 * navigator.clipboard.write is issued in the same tick as the click. Safari
 * rejects clipboard.write with NotAllowedError if the write is deferred past an
 * await, so awaiting the render before writing would push every Safari user onto
 * the download fallback. ClipboardItem accepts a Promise<Blob> and waits for it.
 */
export async function copyAnswerAsImage(params: CopyAnswerAsImageParams): Promise<AnswerImageExportResult> {
  const { node, backgroundColor } = params;
  if (!node) {
    return { status: 'error', error: new Error('No answer node to capture') };
  }

  const filename = params.filename ?? buildAnswerImageFilename();
  const download = params.downloadFn ?? downloadBlob;
  const load = params.loadHtml2CanvasFn ?? loadHtml2Canvas;

  // Start the render without awaiting: the promise is what preserves the Safari
  // user-gesture when passed to ClipboardItem below.
  const renderPromise = renderNodeToPngBlob(node, { backgroundColor }, load);

  if (isClipboardImageWriteSupported()) {
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': renderPromise })]);
      return { status: 'copied' };
    } catch {
      // Clipboard denied or the browser rejected the promise form: fall back to a
      // download using the same in-flight render (never re-render the node).
      try {
        download(await renderPromise, filename);
        return { status: 'downloaded' };
      } catch (renderError) {
        return { status: 'error', error: renderError };
      }
    }
  }

  // No clipboard image support (older browsers): await the render and download.
  try {
    download(await renderPromise, filename);
    return { status: 'downloaded' };
  } catch (error) {
    return { status: 'error', error };
  }
}
